import { useCallback, useRef, useState } from 'react';
import { IContactForms } from '../../redux/initState/InterfacesState';
import { APIWordpress } from '../../redux/services/APIRequestsWordpress';

export type CaptchaMode = 'visible' | 'invisible';

export interface SmartCaptchaController {
  /** Капча сконфигурирована и должна показываться/проверяться. */
  enabled: boolean;
  sitekey: string;
  mode: CaptchaMode;
  /** Текущий токен (пустой, пока не пройдена). */
  token: string;
  /** key для пересоздания виджета при сбросе. */
  resetKey: number;
  /** Видимость невидимого виджета (триггер проверки). */
  visible: boolean;
  /** Текст ошибки капчи для показа в форме. */
  captchaError: string | null;

  // Колбэки для виджета (см. SmartCaptchaBox):
  onSuccess: (token: string) => void;
  onTokenExpired: () => void;
  onNetworkError: () => void;
  onChallengeHidden: () => void;
  onJavascriptError: (error?: any) => void;

  /**
   * Гарантирует наличие токена перед отправкой формы.
   * - капча выключена → '' (форма не блокируется);
   * - visible → текущий токен либо reject с подсветкой ошибки;
   * - invisible → запускает проверку и резолвит токен в onSuccess.
   */
  ensureToken: () => Promise<string>;
  /** Сброс капчи (после успешной отправки, ошибки сервера, смены темы). */
  reset: () => void;
}

const CAPTCHA_ERROR_TEXT = 'Подтвердите, что вы не робот';
const CAPTCHA_RETRY_ERROR_TEXT = 'Не удалось пройти проверку, попробуйте отправить ещё раз';

/**
 * Сколько раз перевыпускаем токен в invisible-режиме, если предпроверка
 * на сервере его не подтвердила (Яндекс иногда выдаёт «битые» токены,
 * которые его же /validate не признаёт).
 */
const PRECHECK_ATTEMPTS = 5;

/**
 * Предохранитель: если виджет капчи не отвечает (например, его CDN заблокирован
 * сетью пользователя и captcha.js не загрузился) — не виснем вечно, а реджектим
 * через таймаут, чтобы кнопка отправки вернулась в активное состояние.
 */
const MINT_TIMEOUT_MS = 20000;

interface PendingResolver {
  resolve: (token: string) => void;
  reject: (reason?: any) => void;
}

/**
 * Управление состоянием Yandex SmartCaptcha. Конфиг (sitekey/mode/enabled)
 * берётся из ACF-опций, прокинутых в форму как contactForms.
 */
export const useSmartCaptcha = (contactForms?: IContactForms): SmartCaptchaController => {
  const sitekey = contactForms?.captcha_sitekey || '';
  const mode: CaptchaMode = contactForms?.captcha_mode === 'invisible' ? 'invisible' : 'visible';
  const enabled = !!contactForms?.captcha_enabled && !!sitekey;

  const [token, setToken] = useState('');
  const [resetKey, setResetKey] = useState(0);
  const [visible, setVisible] = useState(false);
  const [captchaError, setCaptchaError] = useState<string | null>(null);

  const tokenRef = useRef('');
  const pendingRef = useRef<PendingResolver | null>(null);

  const setTokenSafe = useCallback((value: string) => {
    tokenRef.current = value;
    setToken(value);
  }, []);

  const reset = useCallback(() => {
    setTokenSafe('');
    setVisible(false);
    setCaptchaError(null);
    setResetKey((k) => k + 1);
    if (pendingRef.current) {
      pendingRef.current.reject(new Error('captcha-reset'));
      pendingRef.current = null;
    }
  }, [setTokenSafe]);

  const onSuccess = useCallback((value: string) => {
    setTokenSafe(value);
    setCaptchaError(null);
    if (pendingRef.current) {
      pendingRef.current.resolve(value);
      pendingRef.current = null;
      setVisible(false);
    }
  }, [setTokenSafe]);

  const onTokenExpired = useCallback(() => {
    setTokenSafe('');
    setResetKey((k) => k + 1);
  }, [setTokenSafe]);

  const onNetworkError = useCallback(() => {
    console.warn('[captcha] network error from widget');
    if (pendingRef.current) {
      pendingRef.current.reject(new Error('captcha-network-error'));
      pendingRef.current = null;
    }
    setVisible(false);
    setCaptchaError('Не удалось загрузить капчу, попробуйте ещё раз');
  }, []);

  const onChallengeHidden = useCallback(() => {
    // Пользователь закрыл челлендж, не решив капчу.
    console.warn('[captcha] challenge hidden (user closed without solving, or auto-dismissed)');
    if (pendingRef.current) {
      pendingRef.current.reject(new Error('captcha-challenge-hidden'));
      pendingRef.current = null;
    }
    setVisible(false);
  }, []);

  const onJavascriptError = useCallback((error?: any) => {
    // Внутренняя ошибка виджета капчи (в т.ч. при недоступности его скриптов).
    console.error('[captcha] javascript-error from widget', error);
    if (pendingRef.current) {
      pendingRef.current.reject(new Error('captcha-js-error'));
      pendingRef.current = null;
    }
    setVisible(false);
    setCaptchaError(CAPTCHA_RETRY_ERROR_TEXT);
  }, []);

  const mintToken = useCallback((): Promise<string> => {
    return new Promise<string>((resolve, reject) => {
      const timer = setTimeout(() => {
        if (pendingRef.current) {
          pendingRef.current = null;
          setVisible(false);
          setCaptchaError(CAPTCHA_RETRY_ERROR_TEXT);
          console.error('[captcha] mintToken timeout — виджет не ответил, реджектим');
          reject(new Error('captcha-timeout'));
        }
      }, MINT_TIMEOUT_MS);
      // Резолверы оборачиваем, чтобы любой ответ виджета снимал таймаут.
      pendingRef.current = {
        resolve: (token: string) => { clearTimeout(timer); resolve(token); },
        reject: (reason?: any) => { clearTimeout(timer); reject(reason); },
      };
      setVisible(true);
    });
  }, []);

  // Пересоздаём виджет, чтобы следующий execute() выдал новый токен.
  const discardToken = useCallback(() => {
    setTokenSafe('');
    setVisible(false);
    setResetKey((k) => k + 1);
  }, [setTokenSafe]);

  const ensureToken = useCallback(async (): Promise<string> => {
    if (!enabled) {
      console.log('[captcha] ensureToken: disabled → empty token');
      return '';
    }

    if (mode === 'invisible') {
      console.log('[captcha] ensureToken: invisible mode, hasCachedToken=', !!tokenRef.current);
      for (let attempt = 1; attempt <= PRECHECK_ATTEMPTS; attempt++) {
        const minted = !tokenRef.current;
        const token = tokenRef.current || await mintToken();
        console.log(`[captcha] precheck attempt ${attempt}/${PRECHECK_ATTEMPTS}`, {minted, length: token.length});
        if (await APIWordpress.precheckCaptcha(token)) {
          console.log(`[captcha] precheck passed on attempt ${attempt}`);
          return token;
        }
        console.error(`SmartCaptcha: токен не прошёл предпроверку (попытка ${attempt} из ${PRECHECK_ATTEMPTS})`);
        discardToken();
      }
      console.error('[captcha] precheck failed after all attempts (invisible)');
      setCaptchaError(CAPTCHA_RETRY_ERROR_TEXT);
      throw new Error('captcha-precheck-failed');
    }

    // visible: токен появляется после клика пользователя по чекбоксу.
    console.log('[captcha] ensureToken: visible mode, hasToken=', !!tokenRef.current);
    if (tokenRef.current) {
      const token = tokenRef.current;
      if (await APIWordpress.precheckCaptcha(token)) {
        console.log('[captcha] precheck passed (visible)');
        return token;
      }
      console.error('[captcha] precheck failed (visible) → asking to retry');
      discardToken();
      setCaptchaError(CAPTCHA_ERROR_TEXT);
      throw new Error('captcha-precheck-failed');
    }
    console.warn('[captcha] no token yet (user has not checked the box)');
    setCaptchaError(CAPTCHA_ERROR_TEXT);
    throw new Error('captcha-required');
  }, [enabled, mode, mintToken, discardToken]);

  return {
    enabled,
    sitekey,
    mode,
    token,
    resetKey,
    visible,
    captchaError,
    onSuccess,
    onTokenExpired,
    onNetworkError,
    onChallengeHidden,
    onJavascriptError,
    ensureToken,
    reset,
  };
};
